import styled from '@emotion/styled'
import { ExtranetUserDto } from '@pharmac-extranet/extranet-api'
import { useLanguage } from '@pharmac-extranet/shared'
import { PharmacColumn, PharmacContainer, PharmacText } from '@pharmac/ui'
import { useQuery } from '@tanstack/react-query'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'

const InnerContentContainer = styled.div`
  display: flex;
  width: 100%;
  padding: var(--spacing-large) var(--spacing-large);
`

const ColumnContainer = styled(PharmacContainer)`
  width: 100%;
`

type Props = {
  customerUser: ExtranetUserDto
}

export const CustomerUserListDetails = ({ customerUser }: Props) => {
  const { t, key } = useLanguage(['extranet', 'common'])

  const { customer } = useCustomerContext()

  const { data, isLoading } = useQuery({
    queryKey: ['__customerUserListDetails', customerUser],
    queryFn: async () =>
      await api.customers.getCustomerUserRoles(customer!.id, customerUser.keycloakId),
    enabled: !!customer!.id && !!customerUser?.keycloakId,
  })

  if (isLoading) {
    return null
  }

  return (
    <InnerContentContainer>
      <ColumnContainer>
        <PharmacColumn paddingLargeRight>
          <PharmacContainer paddingMediumBottom>
            <PharmacText
              weight="bold"
              paddingSmallBottom
            >
              {t('phoneNumber', { ns: 'common' })}
            </PharmacText>
            <PharmacText>{customerUser.phoneNumber || '-'}</PharmacText>
          </PharmacContainer>
          <PharmacContainer paddingMediumBottom>
            <PharmacText
              weight="bold"
              paddingSmallBottom
            >
              {t('streetName', { ns: 'common' })}
            </PharmacText>
            <PharmacText>{customerUser.streetName || '-'}</PharmacText>
          </PharmacContainer>
          <PharmacContainer paddingMediumBottom>
            <PharmacText
              weight="bold"
              paddingSmallBottom
            >
              {t('postCode', { ns: 'common' })}
            </PharmacText>
            <PharmacText>{customerUser.postCode || '-'}</PharmacText>
          </PharmacContainer>
        </PharmacColumn>
      </ColumnContainer>
      <ColumnContainer>
        <PharmacColumn paddingLargeRight>
          <PharmacContainer paddingMediumBottom>
            <PharmacText
              weight="bold"
              paddingSmallBottom
            >
              {t('townName', { ns: 'common' })}
            </PharmacText>
            <PharmacText>{customerUser.townName || '-'}</PharmacText>
          </PharmacContainer>
          <PharmacContainer paddingMediumBottom>
            <PharmacText
              weight="bold"
              paddingSmallBottom
            >
              {t('country', { ns: 'common' })}
            </PharmacText>
            <PharmacText>{customerUser.countryName || '-'}</PharmacText>
          </PharmacContainer>
        </PharmacColumn>
      </ColumnContainer>
      <ColumnContainer>
        <PharmacContainer paddingMediumBottom>
          <PharmacText
            weight="bold"
            marginSmallBottom
          >
            {t('certificateCardNumber', { ns: 'common' })}
          </PharmacText>
          <PharmacText>{customerUser.certificateCardNumber || '-'}</PharmacText>
        </PharmacContainer>
        <PharmacColumn paddingLargeRight>
          <PharmacContainer paddingMediumBottom>
            <PharmacText weight="bold">{t('common:professionalRoles')}:</PharmacText>
            {customerUser.professionalRoles &&
              customerUser.professionalRoles.map((role) => (
                <PharmacText
                  key={role.id}
                  marginSmallTop
                >
                  {key(role?.displayName)}
                </PharmacText>
              ))}
          </PharmacContainer>
        </PharmacColumn>
      </ColumnContainer>
      <ColumnContainer>
        <PharmacColumn paddingLargeRight>
          <PharmacColumn paddingLargeRight>
            <PharmacContainer paddingMediumBottom>
              <PharmacText weight="bold">{t('user.userRoles', { ns: 'extranet' })}:</PharmacText>
              {data?.roles?.map((a: any) => (
                <PharmacText
                  marginSmallTop
                  key={a.id}
                >
                  {key(a.displayName)}
                </PharmacText>
              ))}
            </PharmacContainer>
          </PharmacColumn>
        </PharmacColumn>
      </ColumnContainer>
    </InnerContentContainer>
  )
}
